import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormLabel,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {CancelButton, PreviousButton} from '../StepperButtons'
import useScreenFlow from '../../hooks/useScreenFlow'
import React, {useContext, useState} from 'react'
import {AppContext} from '../../contexts/AppContextProvider'
import {MaterialResponse, Product, SecondaryDeviceState, YesNoOptions} from '../../contexts/types'
import {useForm} from '../../hooks/useForm'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import ProductSearchInput from "../Inputs/ProductSearchInput";
import CNBSRadioGroupInput from "../Inputs/CNBSRadioGroupInput";
import SelectedProductDisplay from "../SelectedProductDisplay";
import useUnknownProduct from "../../hooks/useUnknownProduct";
import useDefaultRadioOptions from "../../hooks/useDefaultRadioOptions";
import {setSecondaryDevice} from "../../contexts/SecondaryDeviceReducer";
import {setSecondaryDeviceReplacement} from "../../contexts/ReplacementsReducer";
import {getDefaultSecondaryDevice} from "../../contexts/AppStateDefaults";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import http from "../../http";

const SecondaryDevice: React.FC = () => {
    const {screenPaths, navigateTo} = useScreenFlow()
    const {
        complaint,
        dispatch,
        postNotification,
        clearNotification,
        customerNumber,
        countryCode
    } = useContext(AppContext)
    const {t, language} = useContext(OrgCustomizationContext)
    const [isValidatingBatchNum, setIsValidatingBatchNum] =
        useState<boolean>(false)
    const [isBatchNumValid, setIsBatchNumValid] = useState<boolean>(true)
    const [showBatchNumCheck, setShowBatchNumCheck] = useState<boolean>(false)

    const {
        data,
        errors,
        handleChangeEvent,
        validateFields,
        enableOnChangeValidation,
        setFormData
    } = useForm<SecondaryDeviceState>({
        initialValues: {...complaint.secondaryDevice},
        validations: {
            isBatchOrLotNumberKnown: {
                custom: {
                    isValid: (value, data) => {
                        // If there's a complaint on the secondary device, skip validation.
                        if (data.hasSecondaryDeviceComplaint === 'YES') return true
                        if (data.isProductUnknown) return true
                        if (data.hasSecondaryDevice !== 'YES') return true

                        return !!value;
                    },
                    message: t('this_field_is_required')
                }
            },
            batchOrLotNumber: {
                custom: {
                    isValid: (value, data) => {
                        // If there's a complaint on the secondary device, skip validation.
                        if (data.hasSecondaryDeviceComplaint === 'YES') return true
                        if (data.isProductUnknown) return true
                        if (data.isBatchOrLotNumberKnown === 'YES') {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('pt_valid_batch_or_lot_number_is_required'),
                },
            },
            hasSecondaryDevice: {
                required: {
                    value: true,
                    message: t('this_field_is_required')
                }
            },
            hasSecondaryDeviceComplaint: {
                required: {
                    value: (value, data) => !(data.hasSecondaryDevice === 'YES' && !value),
                    message: t('this_field_is_required'),
                },
            },
            isReplacementNeeded: {
                required: {
                    value: (value, data) => {
                        if (data.hasSecondaryDeviceComplaint === 'YES') return true;
                        return !(data.hasSecondaryDevice === 'YES' && !value);
                    },
                    message: t('this_field_is_required')
                }
            },
            selectedProduct: {
                required: {
                    value: (value, data) => !(data.hasSecondaryDevice === 'YES' && !value),
                    message: t('this_field_is_required')
                }
            }
        },
    })

    const {unknownProduct} = useUnknownProduct(complaint.secondaryDevice?.isProductUnknown || false)
    const {yesNoRadioOptions} = useDefaultRadioOptions()
    //const isThirdParty = Boolean(data.selectedProduct && !data.isProductUnknown && !data.selectedProduct.isFirstPartyManufactured)
    const isSameProductType =
        complaint.generalComplaint?.selectedProduct?.productType &&
        data?.selectedProduct?.productType &&
        complaint.generalComplaint.selectedProduct.productType === data.selectedProduct.productType

    const handleValidation = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        let isFormValid = validateFields(data)

        if (isFormValid) {
            if (data.isBatchOrLotNumberKnown === 'YES') {
                isFormValid = isBatchNumValid
            }
        }

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return isFormValid
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        return isFormValid
    }

    const handleSubmit = () => {
        
        if (!handleValidation()) return

        dispatch(setSecondaryDevice(data))
        dispatch(setSecondaryDeviceReplacement(
            data.selectedProduct?.isDiscontinuedOrUnknown
                ? undefined
                : data.isReplacementNeeded === 'YES'
                    ? data.selectedProduct
                    : undefined
        ))

        navigateTo(screenPaths.availableToReturn)
    }

    const batchNumInitialValue: string = complaint.secondaryDevice
        .batchOrLotNumber as string

    const handleBatchNumberOnBlur = () => {

        const cancelTokenSource = axios.CancelToken.source()
        const batchNum = data.batchOrLotNumber

        const validateBatchNum = async (batchNum: string) => {
            try {
                setIsValidatingBatchNum(true)

                let response = await http.get<MaterialResponse>(
                    `/api/Product/Validate?material=${data.selectedProduct?.productId}&batch=${batchNum}&customerNumber=${customerNumber}&countryCode=${countryCode}`,
                    {cancelToken: cancelTokenSource.token})

                if (
                    response &&
                    response.data &&
                    response.data.materialList &&
                    response.data.materialList.length > 0
                ) {
                    if (
                        response.data.materialList[0].batchReturnCode === '300'
                    ) {
                        setIsBatchNumValid(true)
                        setShowBatchNumCheck(true)
                    } else {
                        setIsBatchNumValid(false)
                        setShowBatchNumCheck(false)
                    }
                }

                setIsValidatingBatchNum(false)
            } catch (e) {
            }
        }

        const resetBatchNumState = () => {
            setIsBatchNumValid(true)
            setShowBatchNumCheck(false)
            setIsValidatingBatchNum(false)
        }

        if (batchNum) {
            if (batchNum !== batchNumInitialValue) {
                validateBatchNum(batchNum)
            } else {
                resetBatchNumState()
            }
        } else {
            resetBatchNumState()
        }

        return () => {
            cancelTokenSource.cancel()
        }
    }

    const disableReplacementNeeded = data.hasSecondaryDevice === 'YES' && data.hasSecondaryDeviceComplaint === 'YES';

    return (
        <Grid container>
            <Stack spacing={2}>
                <Typography variant='h5' sx={{mb: 1}}>
                    {t(countryCode === 'jp' && language === 'ja' ? 'sd_secondary_device_jp' : 'sd_secondary_device')}
                </Typography>

                <CNBSRadioGroupInput
                    label={t(countryCode === 'jp' && language === 'ja' ? 'sd_was_there_a_secondary_device_jp' : 'sd_was_there_a_secondary_device')}
                    formControlSx={{pt: 1}}
                    value={data.hasSecondaryDevice}
                    onChange={e => {
                        if (e.target.value !== data.hasSecondaryDevice) {
                            setFormData({
                                ...getDefaultSecondaryDevice(),
                                hasSecondaryDevice: e.target.value as YesNoOptions
                            })
                        }
                    }}
                    options={yesNoRadioOptions}
                    error={errors.hasSecondaryDevice}
                />

                {data.hasSecondaryDevice === 'YES' && <CNBSRadioGroupInput
                    label={t(countryCode === 'jp' && language === 'ja' ? 'sd_is_there_a_complaint_jp' : 'sd_is_there_a_complaint')}
                    formControlSx={{pt: 1}}
                    value={data.hasSecondaryDeviceComplaint}
                    onChange={handleChangeEvent('hasSecondaryDeviceComplaint')}
                    options={yesNoRadioOptions}
                    error={errors.hasSecondaryDeviceComplaint}
                />}

                {data.hasSecondaryDeviceComplaint === 'YES' &&
                    data.hasSecondaryDevice === 'YES' && (
                        <Alert severity='info'>
                            {t('sd_new_complaint')}
                        </Alert>
                    )}
            </Stack>

            {(data.hasSecondaryDevice === 'YES' && !data.selectedProduct) && (
                <Box sx={{pt: 2, width: '100%'}}>
                    <ProductSearchInput
                        searchOldProducts
                        searchCurrentProducts
                        isProductUnknown={data.isProductUnknown}
                        setIsProductUnknown={(val) => val && setFormData({
                            ...data,
                            isProductUnknown: true,
                            selectedProduct: unknownProduct
                        })}
                        value={complaint.secondaryDevice.selectedProduct}
                        onChange={(product) => setFormData({
                            ...data,
                            selectedProduct: product,
                            isProductUnknown: false
                        })}
                    />
                </Box>
            )}
            <>

            {(data.selectedProduct || data.isProductUnknown) &&
                <SelectedProductDisplay
                    product={data.selectedProduct as Product}
                    sx={{mt: 3}}
                    endAdornment={<Button sx={{minHeight: 0, minWidth: 0, padding: 0, textAlign: 'left'}}
                                          onClick={() => setFormData({
                                              ...data,
                                              isProductUnknown: undefined,
                                              selectedProduct: undefined,
                                              batchOrLotNumber: '',
                                              isReplacementNeeded: ''
                                          })}>
                        {t(countryCode === 'jp' && language === 'ja' ? 'click_here_to_change_article_number_jp' : 'click_here_to_change_article_number')}
                    </Button>}
                    />}
                {/* {isThirdParty && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Alert color='error' sx={{ mt: 2 }}>
                            <ThirdPartyError/>
                        </Alert>
                    </Box>
                )} */}
                {isSameProductType && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Alert severity="error" sx={{ mt:2}}>
                            {t('same_product_type_error_message_line1') || 'error'} <br />
                            {t('same_product_type_error_message_line2') || 'error'}
                        </Alert>
                    </Box>
                )}
            </>

            <Stack spacing={2} sx={{mt: 3}}>
                {data.selectedProduct && !disableReplacementNeeded && !data.isProductUnknown &&  <>
                    <Grid item xs={12} sx={{mt: 1}}>
                        <CNBSRadioGroupInput
                            label={t('pt_is_batch_or_lot_number_known')}
                            value={data.isBatchOrLotNumberKnown}
                            onChange={handleChangeEvent('isBatchOrLotNumberKnown')}
                            options={yesNoRadioOptions}
                            error={errors.isBatchOrLotNumberKnown}
                        />
                    </Grid>
                    {data.isBatchOrLotNumberKnown === 'YES' && (
                        <Grid item xs={12} sx={{mt: 0}}>
                            <FormControl sx={{mb: 2}}>
                                <FormLabel sx={{mb: 1}}>
                                    {t('pt_enter_batch_or_lot_number')}:
                                </FormLabel>
                                <TextField
                                    id='batch-lot-number'
                                    error={
                                        typeof errors.batchOrLotNumber !==
                                        undefined || !isBatchNumValid
                                    }
                                    size='small'
                                    // helperText={errors.batchOrLotNumber}
                                    helperText={
                                        !isBatchNumValid ||
                                        errors.batchOrLotNumber
                                            ? t(
                                                'pt_valid_batch_or_lot_number_is_required'
                                            )
                                            : null
                                    }
                                    value={data.batchOrLotNumber}
                                    onChange={handleChangeEvent('batchOrLotNumber')}
                                    inputProps={{maxLength: 10}}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                {isValidatingBatchNum && (
                                                    <CircularProgress size='1rem'/>
                                                )}
                                                {showBatchNumCheck && !isValidatingBatchNum &&
                                                    isBatchNumValid &&
                                                    data.batchOrLotNumber && (
                                                        <CheckIcon
                                                            sx={{
                                                                color: '#666)',
                                                            }}
                                                        />
                                                    )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    onBlur={handleBatchNumberOnBlur}
                                />
                            </FormControl>
                        </Grid>
                    )}
                </>}

                {data.selectedProduct && !disableReplacementNeeded &&  <CNBSRadioGroupInput
                    label={t(countryCode === 'jp' && language === 'ja' ? 'sd_replacement_needed_jp' : 'sd_replacement_needed')}
                    value={disableReplacementNeeded ? 'NO' : data.isReplacementNeeded}
                    error={errors.isReplacementNeeded}
                    onChange={(event, value) => {
                        if (!disableReplacementNeeded) {
                            handleChangeEvent('isReplacementNeeded')(event);
                        }
                    }}
                    options={yesNoRadioOptions}
                    disabled={disableReplacementNeeded}
                />}
            </Stack>




            <Stack direction='row-reverse' spacing={2} sx={{pt: 4, width: '100%'}}>
                <Button
                    size='large'
                    variant='contained'
                    color='primary'
                    disabled={
                        (Boolean(isValidatingBatchNum && data.isBatchOrLotNumberKnown === 'YES') || Boolean(isSameProductType) )
                    }
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setSecondaryDevice(data))}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default SecondaryDevice
